/**
 * PrintDoc → a standalone HTML page that looks like the receipt (pure): the dev
 * "print to PDF" bridge (M3 task 7 demos printing without hardware) opens it and
 * calls the browser's print dialog, so the paper can be saved as a PDF. Fixed-width
 * font at the paper's character width; double width/height scaled with CSS; every
 * copy separated by a cut line. QR codes come from `qrSvg` when the caller has a QR
 * library (the staff app), else a framed placeholder with the data.
 */
import type { PrintDoc } from '@qrkey/contracts';
import { layoutDoc, lineChars, type LaidBlock } from './layout.js';

export interface HtmlRenderOptions {
  /** SVG markup of a QR code for `data` (trusted output of a QR library). */
  readonly qrSvg?: (data: string) => string;
}

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c);
}

function blockHtml(b: LaidBlock, options: HtmlRenderOptions): string {
  switch (b.kind) {
    case 'lines':
      return b.lines
        .map((l) => {
          const classes = [
            l.bold ? 'b' : '',
            l.underline ? 'u' : '',
            l.invert ? 'i' : '',
            l.width === 2 ? 'w2' : '',
            l.height === 2 ? 'h2' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return `<div class="l ${classes}">${escapeHtml(l.text) || '&nbsp;'}</div>`;
        })
        .join('');
    case 'qr': {
      const svg = options.qrSvg?.(b.data);
      const inner = svg ?? `<div class="ph">QR<br>${escapeHtml(b.data)}</div>`;
      return `<div class="qr a-${b.align}" style="--qr:${b.size * 22}px">${inner}</div>`;
    }
    case 'barcode':
      return `<div class="a-${b.align}"><div class="ph">${escapeHtml(b.format)}: ${escapeHtml(b.data)}</div></div>`;
    case 'logo':
      return `<div class="a-${b.align}"><div class="ph">${escapeHtml(b.ref)}</div></div>`;
    case 'cut':
      return '<div class="cut"></div>';
  }
}

export function renderHtml(doc: PrintDoc, options: HtmlRenderOptions = {}): string {
  const chars = lineChars(doc);
  const copy = layoutDoc(doc)
    .map((b) => blockHtml(b, options))
    .join('');
  const copies = Array.from(
    { length: doc.copies },
    (_, i) => `<section class="paper" aria-label="Copy ${i + 1}">${copy}</section>`,
  ).join('');
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(doc.title)}</title>
<style>
  body { margin: 0; padding: 16px; background: #eee; }
  .paper { width: ${chars}ch; margin: 0 auto 24px; padding: 8px 10px; background: #fff; color: #000;
    font: 14px/1.35 ui-monospace, Menlo, Consolas, "Courier New", monospace; box-shadow: 0 1px 4px #0003; }
  .l { white-space: pre; overflow: hidden; }
  .b { font-weight: 700; } .u { text-decoration: underline; } .i { background: #000; color: #fff; }
  .w2 { transform: scaleX(2); transform-origin: left; width: 50%; }
  .h2 { font-size: 200%; line-height: 1.2; }
  .w2.h2 { font-size: 100%; transform: scale(2); transform-origin: left top; height: 2.4em; }
  .qr { display: flex; margin: 6px 0; } .qr svg, .qr .ph { width: var(--qr); height: var(--qr); }
  .a-left { justify-content: flex-start; } .a-center { justify-content: center; text-align: center; }
  .a-right { justify-content: flex-end; text-align: right; }
  .ph { border: 1px dashed #000; padding: 4px; font-size: 11px; overflow-wrap: anywhere; display: inline-block; }
  .cut { border-top: 2px dashed #888; margin: 10px -10px 0; }
  @media print { body { background: #fff; padding: 0; } .paper { box-shadow: none; margin: 0; page-break-after: always; } }
</style></head>
<body>${copies}</body></html>`;
}
